import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {AuthService} from './auth.service';
import {BusyService} from './busy.service';

export interface ForceExpireOptions {
    /** When false, skip cross-tab broadcast (remote tab already cleared). Default true. */
    broadcast?: boolean;
}

/**
 * P22-08: Central hard-logout facade.
 *
 * Credential clearing + cross-tab invalidation live in AuthService.endSession.
 * This service owns busy-state cleanup and navigation to /login.
 */
@Injectable({
    providedIn: 'root',
})
export class SessionExpiredService {
    private expireInProgress = false;

    constructor(
        private authService: AuthService,
        private router: Router,
        private busyService: BusyService,
    ) {
    }

    /**
     * Immediate hard logout after refresh failure / confirmed expiry / user logout.
     * Idempotent: concurrent 401s, guard failures, and cross-tab events share one redirect.
     */
    forceExpire(reason: string = 'session-expired', options?: ForceExpireOptions): void {
        const broadcast = options?.broadcast !== false;

        this.authService.endSession({reason, broadcast});
        this.busyService.forceIdle();

        if (this.expireInProgress || this.router.url === '/login') {
            return;
        }

        this.expireInProgress = true;
        void this.router.navigate(['/login']).finally(() => {
            this.busyService.forceIdle();
            this.expireInProgress = false;
            if (typeof window !== 'undefined') {
                window.setTimeout(() => this.busyService.forceIdle(), 0);
            }
        });
    }

    /** @deprecated Use forceExpire(); retained for call-site compatibility during P22-08. */
    show(): void {
        this.forceExpire('legacy-show');
    }

    /** @deprecated Use forceExpire(); button-driven logout is no longer required. */
    loginAgain(): void {
        this.forceExpire('legacy-login-again');
    }
}
